/**
 * Account — signed-in dashboard.
 *
 * Per redesign decision v1.2: artefacts are grouped BY PROPERTY rather
 * than rendered as a flat pack list. The backend `GET /api/user/packs`
 * still returns flat packs (we don't yet persist propertyId on the pack
 * record), so we group client-side by canonical-ish address — postcode
 * + line1 normalised to lowercase + whitespace-trimmed.
 *
 * Each group renders as a single card with:
 *  - Address (one line, prominent)
 *  - State line summarising what the user can do next
 *  - Primary action button (Continue / View / Start)
 *
 * The "Find existing packs" UX from the previous flat list has been
 * dropped; the new property-file route ("/property/:id") is the public
 * way to discover anything saved against a property.
 *
 * Empty state: links the user to "/" so they start at the new discovery
 * surface (the search bar). The brief calls this "[Find a property]".
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import DeletePackModal from '../components/DeletePackModal';
import { PackListSkeleton } from '../components/ui/Skeleton';

interface PackSummary {
  packId: string;
  address?: string;
  propertyId?: string;
  propertyType?: string;
  userRole?: string;
  productType?: 'planning_report' | 'compliance_pack';
  status: 'active' | 'processing' | 'complete';
  completionPercentage: number;
  isPaid: boolean;
  createdAt: string;
  lastUpdatedAt?: string;
}

interface PropertyGroup {
  /** Stable group key — postcode + line1, lowercased, whitespace-collapsed. */
  key: string;
  displayAddress: string;
  packs: PackSummary[];
  /** Most-recently-touched timestamp across the group. */
  lastTouchedAt: string;
  /** Optional propertyId attached to one of the packs (when backend has it). */
  propertyId?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Compute a canonical group key from a free-form address string. We split
 * on commas and use the LAST two segments as `(line1, postcode)` plus
 * the first segment for line1 — best-effort because the backend doesn't
 * yet attach a propertyId to every pack record.
 */
function groupKey(address?: string): string {
  if (!address) return '__no_address__';
  const norm = address.trim().toLowerCase().replace(/\s+/g, ' ');
  return norm;
}

function groupPacks(packs: PackSummary[]): PropertyGroup[] {
  const map = new Map<string, PropertyGroup>();
  for (const pack of packs) {
    const key = groupKey(pack.address);
    const existing = map.get(key);
    const touchedAt = pack.lastUpdatedAt || pack.createdAt;
    if (existing) {
      existing.packs.push(pack);
      if (touchedAt > existing.lastTouchedAt) existing.lastTouchedAt = touchedAt;
      if (pack.propertyId && !existing.propertyId) existing.propertyId = pack.propertyId;
    } else {
      map.set(key, {
        key,
        displayAddress: pack.address || 'Unnamed property',
        packs: [pack],
        lastTouchedAt: touchedAt,
        propertyId: pack.propertyId,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.lastTouchedAt.localeCompare(a.lastTouchedAt));
}

/**
 * Pick the dominant pack to surface as the primary CTA. Order:
 *   1. paid Buyer's Report  → "View Buyer's Report"
 *   2. in-progress Seller's Pack → "Continue Seller's Pack"
 *   3. anything else → "View pack"
 */
function dominantPack(group: PropertyGroup): PackSummary {
  const paidBuyer = group.packs.find((p) => p.productType === 'planning_report' && p.isPaid);
  if (paidBuyer) return paidBuyer;
  const inProgressSeller = group.packs.find((p) => p.userRole === 'seller' && !p.isPaid);
  if (inProgressSeller) return inProgressSeller;
  return group.packs[0];
}

function summariseGroup(group: PropertyGroup): string {
  const pack = dominantPack(group);
  const updated = pack.lastUpdatedAt || pack.createdAt;
  const updatedLabel = new Date(updated).toLocaleDateString('en-GB');
  if (pack.productType === 'planning_report' && pack.isPaid) {
    return `Buyer's Report ready · updated ${updatedLabel}`;
  }
  if (pack.userRole === 'seller') {
    return `Selling · pack ${pack.completionPercentage}% complete · updated ${updatedLabel}`;
  }
  if (pack.userRole === 'buyer') {
    return `Buyer's Report · ${pack.status} · updated ${updatedLabel}`;
  }
  return `Updated ${updatedLabel}`;
}

function primaryCta(group: PropertyGroup): { label: string; href: string } {
  const pack = dominantPack(group);
  if (pack.productType === 'planning_report' && pack.isPaid) {
    return { label: 'View Buyer\'s Report', href: `/report/planning/${encodeURIComponent(pack.packId)}` };
  }
  if (pack.userRole === 'seller') {
    return { label: 'Continue Seller\'s Pack', href: `/account/packs/${encodeURIComponent(pack.packId)}` };
  }
  return { label: 'View pack', href: `/account/packs/${encodeURIComponent(pack.packId)}` };
}

export default function Account() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isAuthenticated, logout } = useAuth();

  const [packs, setPacks] = useState<PackSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalPack, setDeleteModalPack] = useState<PackSummary | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch user's packs
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchPacks = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/user/packs`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          setPacks(data.packs || []);
        } else if (response.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setError('Failed to load packs');
        }
      } catch (err) {
        console.error('Error fetching packs:', err);
        setError('Failed to load packs');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchPacks();
  }, [isAuthenticated, navigate]);

  const groups = useMemo(() => groupPacks(packs), [packs]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleDeletePack = async (packId: string) => {
    const response = await fetch(`${API_BASE_URL}/api/packs/${packId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete pack');
    }

    setPacks(packs.filter((p) => p.packId !== packId));
    setDeleteModalPack(null);
  };

  const firstName = user?.email?.split('@')[0];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="container-main section-spacing">
          <div className="space-y-4">
            <PackListSkeleton count={2} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null; // Will redirect
  }

  return (
    <>
      <Helmet>
        <title>Your Account | UKPropertyChecks</title>
        <meta name="description" content="Your saved properties at UKPropertyChecks." />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/account" />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main className="min-h-screen bg-slate-50">
        <div className="container-main section-spacing">
          {/* Welcome header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-slate-950 mb-1">
                Welcome back, {firstName}
              </h1>
              <p className="text-slate-600">{user.email}</p>
            </div>
            <button onClick={handleLogout} className="btn-secondary self-start">
              Sign out
            </button>
          </div>

          {/* Property list */}
          <section aria-labelledby="properties-heading">
            <div className="flex items-baseline justify-between mb-4">
              <h2
                id="properties-heading"
                className="text-lg font-semibold text-slate-950"
              >
                Your properties{groups.length > 0 ? ` (${groups.length})` : ''}
              </h2>
            </div>

            {isLoading ? (
              <PackListSkeleton count={3} />
            ) : error ? (
              <div className="rounded-xl border border-slate-200 bg-white p-6 text-red-700" role="alert">
                {error}
              </div>
            ) : groups.length === 0 ? (
              <div
                className="rounded-xl border border-slate-200 bg-white p-6 text-center"
                data-testid="account-empty"
              >
                <p className="text-slate-700 mb-4">
                  You haven't saved any properties yet.
                </p>
                <Link to="/" className="btn-primary no-underline">
                  Find a property
                </Link>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="property-groups">
                {groups.map((group) => {
                  const cta = primaryCta(group);
                  const dominant = dominantPack(group);
                  return (
                    <li
                      key={group.key}
                      className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6"
                      data-testid="property-group"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-slate-950 break-words">
                            {group.displayAddress}
                          </h3>
                          <p className="mt-1 text-sm text-slate-700">
                            {summariseGroup(group)}
                          </p>
                          {group.packs.length > 1 && (
                            <p className="mt-1 text-xs text-slate-700">
                              {group.packs.length} pack{group.packs.length === 1 ? '' : 's'} on file
                            </p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                          {/* btn-secondary (not primary) — Account renders many
                              property cards and the design system caps the
                              page at a single primary CTA. The primary on
                              this surface is the empty-state "Find a property"
                              button, which is mutually exclusive with this list. */}
                          <Link to={cta.href} className="btn-secondary btn-sm no-underline">
                            {cta.label}
                          </Link>
                          <button
                            onClick={() => setDeleteModalPack(dominant)}
                            className="btn-ghost text-red-600 hover:bg-red-50 border border-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {groups.length > 0 && (
              <div className="mt-6 text-center">
                <Link to="/" className="text-emerald-700 hover:text-emerald-800 underline">
                  + Add another property
                </Link>
              </div>
            )}
          </section>
        </div>

        {/* Delete confirmation modal */}
        {deleteModalPack && (
          <DeletePackModal
            packId={deleteModalPack.packId}
            address={deleteModalPack.address}
            isOpen={!!deleteModalPack}
            onClose={() => setDeleteModalPack(null)}
            onConfirm={() => handleDeletePack(deleteModalPack.packId)}
          />
        )}
      </main>
    </>
  );
}
